import { formatMoney } from "@bw/shared/utils";
import type { PlayerTransactionReceiptResponse } from "@bw/shared/types/generated";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import { Page } from "../../../common/components/Page";
import { useTransactionHistory } from "../hooks/useTransactionHistory";
import { transactionLabel } from "../transactionPresentation";

/**
 * TransactionsPage (/me/transactions) — the player's payment history, read-only.
 *
 * Reads the caller's history via useTransactionHistory (GET /transactions/history,
 * SERVER-SCOPED — no uid is ever passed). Each row shows ONLY TransactionResponse-
 * backed facts: the FORMATTED date (created_at), a human description/type, the
 * AMOUNT via formatMoney(amount, currency) (the field is a Pydantic Decimal =
 * number|string — NEVER arithmetic on it), and the server status.
 *
 * Honesty: newest-first is the SERVER's order (the page does not re-sort); a raw
 * transaction_id / user_id / facility_id uuid is NEVER rendered as visible text;
 * an absent/null amount renders formatMoney's em-dash "—", never NaN / a
 * fabricated $0.00.
 *
 * No toast lib: loading is role="status", error is role="alert", and the empty
 * state is the honest "No transactions yet".
 *
 * The AppShell supplies no page title, so this routed page OWNS its single <h1>
 * via <Page title="Transactions"> (one h1 per page).
 */

/** Human "Jun 1, 2026" for a transaction's created_at, best-effort. */
function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function TransactionsPage() {
  const history = useTransactionHistory();

  return (
    <Page title="Transactions">
      <Stack spacing={2}>
        {history.isLoading ? (
          <Stack
            direction="row"
            spacing={1.5}
            role="status"
            sx={{ alignItems: "center" }}
          >
            <CircularProgress size={20} />
            <Typography color="text.secondary">
              Loading your transactions…
            </Typography>
          </Stack>
        ) : history.isError ? (
          <Alert severity="error">
            We couldn&apos;t load your transactions. Please try again.
          </Alert>
        ) : !history.data || history.data.length === 0 ? (
          <Typography color="text.secondary">No transactions yet</Typography>
        ) : (
          <List
            disablePadding
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {history.data.map((txn) => (
              <TransactionRow key={txn.transaction_id} txn={txn} />
            ))}
          </List>
        )}
      </Stack>
    </Page>
  );
}

function TransactionRow({ txn }: { txn: PlayerTransactionReceiptResponse }) {
  const label = transactionLabel(txn);
  return (
    <ListItem component="li" disablePadding divider>
      <ListItemButton
        component={RouterLink}
        to={`/me/transactions/${txn.transaction_id}`}
        sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
            <Typography
              component="time"
              variant="body2"
              color="text.secondary"
              dateTime={txn.created_at}
            >
              {formatDate(txn.created_at)}
            </Typography>
          </Box>
          <Stack spacing={0.75} sx={{ alignItems: "flex-end" }}>
            <Typography sx={{ fontWeight: 700 }}>
              {formatMoney(txn.amount, txn.currency)}
            </Typography>
            <Chip label={txn.status} size="small" variant="outlined" />
          </Stack>
        </Stack>
      </ListItemButton>
    </ListItem>
  );
}
